import React from "react";
import { Building2, Package } from "lucide-react";
import { useNavigate } from "react-router";
import type { Product } from "../types";
import { GlassCard } from "./GlassCard";

interface ProductCardProps {
  product: Product;
  variant?: "compact" | "full";
  /** Exhibitor name — shown inside the card when listing across companies. */
  companyName?: string;
}

export function ProductCard({
  product,
  variant = "full",
  companyName,
}: ProductCardProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/products/${product.id}`);
  };

  const thumb = product.images?.[0];

  if (variant === "compact") {
    return (
      <GlassCard
        variant="subtle"
        padding="sm"
        className="cursor-pointer active:scale-[0.98] transition-transform"
        onClick={handleClick}
      >
        <div className="flex items-start gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
            {thumb ? (
              <img src={thumb} alt="" className="w-full h-full object-cover" />
            ) : (
              <Package size={16} className="text-amber-600" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] text-gray-900 line-clamp-1 mb-0.5">
              {product.name}
            </div>
            {companyName && (
              <div className="text-[11px] text-gray-500 truncate mb-0.5">
                {companyName}
              </div>
            )}
            <div className="text-[11px] text-gray-500 line-clamp-2">
              {product.description}
            </div>
            {product.price?.trim() && (
              <div className="text-[11px] text-emerald-700 mt-0.5 truncate">
                {product.price}
              </div>
            )}
          </div>
        </div>
      </GlassCard>
    );
  }

  return (
    <GlassCard
      variant="default"
      padding="md"
      className="cursor-pointer active:scale-[0.98] transition-transform"
      onClick={handleClick}
    >
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
          {thumb ? (
            <img src={thumb} alt="" className="w-full h-full object-cover" />
          ) : (
            <Package size={22} className="text-amber-600" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-[14px] text-gray-900 mb-0.5 line-clamp-2">
            {product.name}
          </h3>

          {companyName && (
            <p className="flex items-center gap-1 text-[12px] text-gray-500 mb-1.5 truncate">
              <Building2 size={12} className="text-indigo-400 flex-shrink-0" />
              <span className="truncate">{companyName}</span>
            </p>
          )}

          {product.description?.trim() && (
            <p className="text-[13px] text-gray-600 line-clamp-2 mb-2">
              {product.description}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-1.5">
            {product.category?.trim() && (
              <div className="text-[11px] text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full inline-block max-w-full truncate">
                {product.category}
              </div>
            )}
            {product.price?.trim() && (
              <div className="text-[11px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full inline-block max-w-full truncate">
                {product.price}
              </div>
            )}
          </div>
        </div>
      </div>
    </GlassCard>
  );
}
